import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Card } from '../../components/common/Card';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { getErrorMessage, getValidationErrors } from '../../services/api';
import { authService } from '../../services/auth.service';
import { useDomainRedirect } from '../../hooks';

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // Redirect to OKR Hub domain if on marketing site
  // The marketing site (hr4india.com) is hosted on S3 without a backend API
  useDomainRedirect();

  // Get success message from navigation state (e.g., after password reset)
  const stateMessage = (location.state as { message?: string })?.message;

  // Check for query parameters
  const searchParams = new URLSearchParams(location.search);
  const isActivated = searchParams.get('activated') === 'true';
  const activationErrorCode = searchParams.get('activation_error');
  const loginToken = searchParams.get('loginToken');
  const redirectPath = searchParams.get('redirect');

  // Map error codes to user-friendly messages
  const getActivationErrorMessage = (code: string | null): string | null => {
    if (!code) return null;
    const errorMessages: Record<string, string> = {
      'TOKEN_EXPIRED': 'Your activation link has expired. Please request a new one.',
      'TOKEN_INVALID': 'Invalid activation link. Please check your email or request a new link.',
      'ALREADY_ACTIVATED': 'This account has already been activated. You can sign in.',
      'ACTIVATION_FAILED': 'Activation failed. Please try again or contact support.',
    };
    return errorMessages[code] || errorMessages.ACTIVATION_FAILED;
  };

  const activationError = getActivationErrorMessage(activationErrorCode);

  // Determine the success message to show
  const successMessage = isActivated
    ? 'Your account has been activated successfully! You can now sign in.'
    : stateMessage;

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    organizationName: '',
    rememberMe: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string>(activationError || '');
  const [showSuccessMessage, setShowSuccessMessage] = useState(!!successMessage);
  const [tokenExchangeInProgress, setTokenExchangeInProgress] = useState(!!loginToken);

  // One-time login token exchange (mobile → web SSO)
  const tokenExchangeAttempted = useRef(false);
  useEffect(() => {
    if (!loginToken || tokenExchangeAttempted.current) return;
    tokenExchangeAttempted.current = true;

    const exchange = async () => {
      try {
        await authService.exchangeLoginToken(loginToken);
        // Clean URL and redirect
        const target = redirectPath || '/dashboard';
        window.history.replaceState({}, document.title, location.pathname);
        window.location.href = target;
      } catch (err) {
        setTokenExchangeInProgress(false);
        setApiError('Login link expired or invalid. Please sign in manually.');
        window.history.replaceState({}, document.title, location.pathname);
      }
    };

    exchange();
  }, [loginToken, redirectPath, location.pathname]);

  // Clear the success message from navigation state and URL params after displaying
  useEffect(() => {
    if (successMessage || activationErrorCode) {
      // Clear the state and URL params to prevent showing the message again on refresh
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [successMessage, activationErrorCode, location.pathname]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    if (!formData.organizationName) {
      newErrors.organizationName = 'Organization is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    setApiError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setApiError('');

    try {
      await login({
        email: formData.email,
        password: formData.password,
        organizationName: formData.organizationName
      });
      // Navigation is handled by AuthContext
    } catch (error) {
      console.error('Login error:', error);
      // Extract field-level validation errors if any
      const validationErrors = getValidationErrors(error);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(prev => ({ ...prev, ...validationErrors }));
      }
      // Set the main error message
      setApiError(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading spinner during token exchange
  if (tokenExchangeInProgress) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-500 via-primary-600 to-stone-800">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-white text-lg">Signing you in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-500 via-primary-600 to-stone-800 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20 backdrop-blur">
              <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>O</span>
            </div>
          </div>
          <h1 className="text-4xl font-semibold text-white mb-2" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>OKR Hub</h1>
          <p className="text-primary-100">Sign in to your account</p>
        </div>

        <Card className="p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {showSuccessMessage && successMessage && (
              <div className="bg-success-50 border border-success-200 text-success-700 px-4 py-3 rounded-lg text-sm flex items-center justify-between">
                <span>{successMessage}</span>
                <button
                  type="button"
                  onClick={() => setShowSuccessMessage(false)}
                  className="text-success-500 hover:text-success-700 ml-2"
                >
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            )}

            {apiError && (
              <div className="bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded-lg text-sm">
                {apiError}
              </div>
            )}

            <Input
              label="Email Address"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
              placeholder="you@example.com"
              autoComplete="email"
              required
            />

            <Input
              label="Password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
              placeholder="Enter your password"
              autoComplete="current-password"
              required
            />

            <Input
              label="Organization"
              type="text"
              name="organizationName"
              value={formData.organizationName}
              onChange={handleChange}
              error={errors.organizationName}
              placeholder="e.g., Real Test Organization"
              helperText="Enter your organization name"
              required
            />

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="rememberMe"
                  name="rememberMe"
                  type="checkbox"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-stone-300 rounded"
                />
                <label htmlFor="rememberMe" className="ml-2 block text-sm text-stone-700">
                  Remember me
                </label>
              </div>

              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                Forgot password?
              </button>
            </div>

            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <LoadingSpinner size="sm" className="mr-2" />
                  Signing in...
                </span>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>
        </Card>

        <div className="mt-6 text-center">
          <p className="text-sm text-primary-100">
            Don't have an account?{' '}
            <span className="text-white font-medium">
              Contact your administrator
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};
